import { Component, HostBinding, Inject, ElementRef } from '@angular/core';
import { TOOLTIP_DATA } from './nv-tooltip.directive';
import { isTooltipContent, ITooltipContent, ITooltipData } from './nv-tooltip.interface';

/**
 *
 * Use the `nvTooltip` directive on any element to create a tooltip.
 *
 * Pass in a `tooltipData` prop to populate the tooltip (must meet contract of ITooltipData).
 *
 * The tooltip component looks at the type and shape of the `content` parameter in `tooltipData`
 * to determine how to style the tooltip.
 *
 * If `content` is a `string`, then the tooltip will display as plain text.
 *
 * If the `content` is an object (that meets ITooltipContent), it will check for a `rowData` parameter, and optional `header` parameter.
 * The tooltip will be styled accordingly sased on the shape of `rowData`.
 *
 * All you the developer need to do is pass in the appropriate `rowData` 2D-array, an the styling will be applied automatically.
 *
 * ```
 * interface ITooltipData {
 *   type?: 'sync' | 'async';
 *   calc?: string;
 *   content?: string | ITooltipContent;
 * }
 *
 * interface ITooltipContent {
 *   headers?: Array<string>;
 *   rowData: Array<Array<string>>;
 * }
 * ```
 *
 */
@Component({
  selector: 'nv-tooltip',
  template: require('./nv-tooltip.component.html'),
  styles: [require('./nv-tooltip.component.scss')],
})
export class NvTooltipComponent {
  constructor(@Inject(TOOLTIP_DATA) public tooltipData: ITooltipData, private tooltipElementRef: ElementRef) {}

  private maxLabelLen = 24;

  get contentIsString(): boolean {
    return typeof this.tooltipData.content === 'string';
  }

  get contentIsValid(): boolean {
    if (typeof this.tooltipData.content === 'string') return true;
    else return isTooltipContent(this.tooltipData.content);
  }

  @HostBinding('class.nv-tooltip-component') isTooltipClass = true;

  // The default styling assumes a plain string
  @HostBinding('class.nv-tooltip-is_string') isStringClass = this.isString;

  // Style the first column differently if it's the only column vs first of many
  @HostBinding('class.nv-tooltip-is_one_column') isOneColClass = this.isSingleColumn;

  // Style each 'row' as a stacked list if the label is too long
  @HostBinding('class.nv-tooltip-is_stacked') isStackedClass = this.isStacked;

  // Style things differently if there's more than two columns
  @HostBinding('class.nv-tooltip-is_table') isTableClass = this.isTable;

  ngAfterContentInit(): void {
    this.setTableColumnCustomProperty();
  }

  setTableColumnCustomProperty() {
    // Need to explicitly state the number of columns for CSS grid to work
    // See scss file for more details
    this.tooltipElementRef.nativeElement.style.setProperty('--table-column-count', this.countColumns);
  }

  get isString() {
    return this.contentIsString;
  }

  get isSingleColumn(): boolean {
    if (typeof this.tooltipData.content === 'string' || !this.contentIsValid) {
      return false;
    } else {
      return this.tooltipData.content.rowData.every(row => row.length === 1);
    }
  }

  // If the label of a 2 column list is too long (maxLabelLen),
  // the `value` (column 2) should appear below the label.
  get isStacked(): boolean {
    if (!this.contentIsValid || typeof this.tooltipData.content === 'string' || this.isSingleColumn || this.isTable) {
      return false;
    } else {
      // if some have a label & it's long
      const rowLabels = this.tooltipData.content.rowData.map(row => row[0]);
      const longestLabelLength = Math.max(...rowLabels.map(item => item.length));
      return longestLabelLength >= this.maxLabelLen;
    }
  }

  get isTable(): boolean {
    if (typeof this.tooltipData.content === 'string' || !this.contentIsValid) {
      return false;
    } else {
      return this.tooltipData.content.rowData.some(row => row.length > 2);
    }
  }

  get countColumns(): number {
    if (this.isTable) {
      const content = this.tooltipData.content as ITooltipContent;
      return content.rowData[0].length;
    } else {
      return 0;
    }
  }
}
